import { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../api';

// Documents detected inside a compilation PDF + the attorney's rulings
// (plans/BY_DOCUMENT_INVENTORY_VIEW_PROPOSAL.md §3.4, §7c). Shared by the
// inventory panel and the viewer so both surfaces see the same effective
// segments; a ruling on either fires `dc:segments-changed` so the other refreshes.
export const SEGMENTS_CHANGED_EVENT = 'dc:segments-changed';

export function announceSegmentsChanged(documentId, source = 'viewer') {
  try { window.dispatchEvent(new CustomEvent(SEGMENTS_CHANGED_EVENT, { detail: { documentId, source } })); } catch { /* no window */ }
}

export default function useDocumentSegments(documentId, { enabled = true } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const aliveRef = useRef(true);

  const reload = useCallback(async () => {
    if (!documentId || !enabled) return null;
    const d = await api.get(`/documents/${documentId}/segments`);
    if (aliveRef.current) setData(d);
    return d;
  }, [documentId, enabled]);

  useEffect(() => {
    aliveRef.current = true;
    if (!documentId || !enabled) { setData(null); return undefined; }
    setLoading(true); setError(null);
    reload().catch((e) => { if (aliveRef.current) setError(e?.message || 'Could not load detected documents'); })
      .finally(() => { if (aliveRef.current) setLoading(false); });
    const onChanged = (e) => { if (e?.detail?.documentId === documentId && e?.detail?.source !== 'viewer') reload().catch(() => {}); };
    window.addEventListener(SEGMENTS_CHANGED_EVENT, onChanged);
    return () => { aliveRef.current = false; window.removeEventListener(SEGMENTS_CHANGED_EVENT, onChanged); };
  }, [documentId, enabled, reload]);

  const rule = useCallback(async (action, page, extra = {}) => {
    if (!documentId) return null;
    setBusy(true); setError(null);
    try {
      const d = await api.post(`/documents/${documentId}/segments/decisions`, { action, page, ...extra });
      if (aliveRef.current) setData(d);
      announceSegmentsChanged(documentId);
      return d;
    } catch (e) {
      if (aliveRef.current) setError(e?.message || `Could not ${action}`);
      throw e;
    } finally {
      if (aliveRef.current) setBusy(false);
    }
  }, [documentId]);

  // Undo = the server appends the inverse of the latest ruling (plan §7d). Undoing an undo is a redo.
  const undo = useCallback(async () => {
    if (!documentId) return null;
    setBusy(true); setError(null);
    try {
      const d = await api.post(`/documents/${documentId}/segments/decisions/undo`, {});
      if (aliveRef.current) setData(d);
      announceSegmentsChanged(documentId);
      return d;
    } catch (e) {
      if (aliveRef.current) setError(e?.message || 'Could not undo');
      throw e;
    } finally {
      if (aliveRef.current) setBusy(false);
    }
  }, [documentId]);

  return { data, loading, error, busy, reload, rule, undo };
}

// What a ruling did, in the attorney's words — the undo toast's text.
export function rulingLabel(action, page, extra = {}, seg = null) {
  const name = seg?.title ? `“${String(seg.title).slice(0, 48)}”` : `the document starting on page ${page}`;
  switch (action) {
    case 'dismiss': case 'merge': return `Merged ${name} into the document above`;
    case 'add': case 'split': return `A document now starts on page ${page}`;
    case 'move': return `${name} now starts on page ${extra.to_page}`;
    case 'rename': return `Renamed to “${String(extra.value || '').slice(0, 48)}”`;
    case 'retype': return `Type changed to ${String(extra.value || '').replace(/_/g, ' ')}`;
    case 'accept': return 'Kept as a separate document';
    default: return 'Changed';
  }
}
